import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../Data/AuthContext";

const ICONS = {
  overview: "M3 12h7V3H3v9Zm0 9h7v-7H3v7Zm11 0h7V12h-7v9Zm0-18v7h7V3h-7Z",
  users: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm13 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  programs: "M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z",
  profile: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
  site: "M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0Zm0 0h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18Z",
};

function Icon({ d }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

const NAV = [
  { to: "/AdminDash/mainPage", label: "Overview", icon: ICONS.overview },
  { to: "/AdminDash/userPage", label: "Users", icon: ICONS.users },
  { to: "/AdminDash/programPage", label: "Programs", icon: ICONS.programs },
  { to: "/AdminDash/profilePage", label: "Profile", icon: ICONS.profile },
];

const TITLES = {
  mainPage: "Overview",
  userPage: "User accounts",
  programPage: "Programs",
  profilePage: "Your profile",
};

export default function DashAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [ready, setReady] = useState(false);

  // The dashboard index has no content of its own; send it to the overview
  // rather than rendering an empty Outlet.
  useEffect(() => {
    if (location.pathname === "/AdminDash" || location.pathname === "/AdminDash/") {
      navigate("/AdminDash/mainPage", { replace: true });
    } else {
      setReady(true);
    }
  }, [location.pathname, navigate]);

  async function handleLogout() {
    await logout();
    navigate("/signin", { replace: true });
  }

  const segment = location.pathname.split("/").filter(Boolean).pop();
  const title = TITLES[segment] || "Dashboard";
  const initials = `${(user?.firstname || "")[0] || ""}${(user?.lastname || "")[0] || ""}`.toUpperCase() || "A";

  return (
    <div className="es-dash">
      <a className="es-skip-link" href="#dash-content">Skip to dashboard content</a>

      <aside className="es-dash__side">
        <Link to="/" className="es-dash__brand">
          <span className="es-brand__mark" aria-hidden="true">ES</span>
          <span><strong>Edu</strong><span style={{ color: "var(--es-sand-400)" }}>Special</span></span>
        </Link>

        <nav className="es-dash__nav" aria-label="Dashboard sections">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} className="es-dash__link">
              <Icon d={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="es-dash__foot">
          <Link to="/" className="es-dash__link">
            <Icon d={ICONS.site} />
            View public site
          </Link>
        </div>
      </aside>

      <div className="es-dash__main">
        <header className="es-dash__bar">
          <div style={{ minWidth: 0 }}>
            <p className="es-eyebrow" style={{ margin: 0 }}>Administration</p>
            <h1 style={{
              margin: 0,
              fontFamily: "var(--es-font-display)",
              fontWeight: 400,
              fontSize: "1.3rem",
              color: "var(--es-teal-900)",
            }}>
              {title}
            </h1>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.85rem" }}>
            <div style={{ textAlign: "right", lineHeight: 1.25 }} className="es-muted">
              <div style={{ fontWeight: 600, color: "var(--es-ink)", fontSize: "0.9rem" }}>
                {user?.firstname} {user?.lastname}
              </div>
              <div style={{ fontSize: "0.78rem" }}>Administrator</div>
            </div>
            <span className="es-avatar" aria-hidden="true" style={{ cursor: "default" }}>{initials}</span>
            <button type="button" className="es-btn es-btn--ghost es-btn--sm" onClick={handleLogout}>
              Sign out
            </button>
          </div>
        </header>

        <div className="es-dash__body" id="dash-content">
          {ready && <Outlet />}
        </div>
      </div>
    </div>
  );
}
